import type { z } from "zod";
import type {
  CoerceCtx,
  CoerceResult,
  FieldSettingsMap,
  FieldType,
  FieldValueMap,
  FormatCtx,
  RowMeta,
} from "./types";

/**
 * Opérateurs de filtre. `is_empty` / `is_not_empty` valent pour tous les
 * types et sont ajoutés par le moteur (cf. `query.ts`) : les modules ne
 * déclarent que les leurs.
 */
export type OperatorId =
  | "is_empty"
  | "is_not_empty"
  | "eq"
  | "neq"
  | "contains"
  | "not_contains"
  | "starts_with"
  | "ends_with"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "is_any_of"
  | "is_none_of"
  | "has_all_of"
  | "before"
  | "after"
  | "on_or_before"
  | "on_or_after"
  | "is_checked"
  | "is_unchecked"
  | "is_done"
  | "is_not_done";

/**
 * Un opérateur ne voit jamais de valeur vide : le moteur répond seul pour
 * `null` (faux, sauf pour les opérateurs « négatifs » qui déclarent
 * `matchesEmpty`).
 */
export type FilterOperator<T extends FieldType, O = unknown> = {
  id: OperatorId;
  // `none` : pas d'opérande. `value` : une valeur du type du champ, convertie
  // par son `coerce` (« demain », un nom d'option). `values` : une liste de
  // valeurs du type (un opérande par élément). `text` : du texte brut.
  // `day` : un jour "YYYY-MM-DD", « aujourd'hui » compris.
  operand: "none" | "value" | "values" | "text" | "day";
  matchesEmpty?: boolean;
  test: (
    value: FieldValueMap[T],
    operand: O,
    settings: FieldSettingsMap[T],
    ctx: FilterCtx,
  ) => boolean;
};

/** Le fuseau du lecteur : « aujourd'hui » et le jour d'un instant en dépendent. */
export type FilterCtx = { timeZone: string };

export type AggregateId =
  | "count"
  | "count_empty"
  | "count_not_empty"
  | "count_unique"
  | "percent_empty"
  | "percent_not_empty"
  | "sum"
  | "average"
  | "median"
  | "min"
  | "max"
  | "range"
  | "count_checked"
  | "count_unchecked"
  | "percent_checked"
  | "percent_unchecked"
  | "earliest"
  | "latest";

/**
 * Tout ce qu'un type de champ sait faire, hors React. Un module par type,
 * réunis dans `registry.ts` : aucun `switch` sur le type ailleurs.
 */
export interface FieldKind<T extends FieldType> {
  type: T;
  // Réglages : validation, valeurs par défaut, clés inconnues retirées.
  // Ajouter un réglage = une clé avec `.default()` ou `.optional()`, sans
  // migration des champs existants.
  settings: z.ZodType<FieldSettingsMap[T]>;
  // Forme stockée d'une valeur non vide.
  value: z.ZodType<FieldValueMap[T]>;
  // Lecture seule : calculé (`compute`), `coerce` refuse toute écriture.
  readOnly?: boolean;
  compute?: (meta: RowMeta) => FieldValueMap[T] | null;
  /**
   * Entrée brute → valeur stockée : saisie, collage, CSV, sortie de LLM,
   * texte pivot d'une conversion. Ne voit jamais `null` / `undefined` / ""
   * (vides par définition, traités par `coerceFieldValue`).
   */
  coerce: (
    input: unknown,
    settings: FieldSettingsMap[T],
    ctx: CoerceCtx,
  ) => CoerceResult<T>;
  // Une valeur stockée qui ne dit rien (texte blanc, liste vide…).
  isEmpty?: (value: FieldValueMap[T]) => boolean;
  /**
   * Le texte d'une valeur non vide. Un seul formatage pour l'UI, la
   * recherche, le LLM et l'export : le serveur et l'écran disent pareil.
   * En style `plain`, `coerce(format(v))` doit retrouver `v`.
   */
  format: (
    value: FieldValueMap[T],
    settings: FieldSettingsMap[T],
    ctx: FormatCtx,
  ) => string;
  compare: (
    a: FieldValueMap[T],
    b: FieldValueMap[T],
    settings: FieldSettingsMap[T],
  ) => number;
  filters: FilterOperator<T, never>[];
  aggregates: AggregateId[];
  // Valeur numérique pour somme, moyenne, médiane, plage.
  toNumber?: (value: FieldValueMap[T]) => number;
  // Groupes d'une valeur, pour le kanban (une ligne peut être dans plusieurs
  // groupes : multi_select). Absent : le type ne se regroupe pas.
  groupKeys?: (value: FieldValueMap[T]) => string[];
  // Le groupe d'une valeur vide, si ce n'est pas le groupe « vide »
  // (une case jamais cochée est décochée).
  emptyGroupKey?: string;
  // Ordre et libellés des groupes possibles, vide compris en dernier.
  groups?: (settings: FieldSettingsMap[T]) => { key: string; label: string }[];
}

export function ok<T extends FieldType>(
  value: FieldValueMap[T] | null,
): CoerceResult<T> {
  return { ok: true, value };
}

export function fail<T extends FieldType>(
  reason: string,
  extra?: { unknownOptions?: string[] },
): CoerceResult<T> {
  return { ok: false, reason, ...extra };
}

/** Comparaison de chaînes pour les tris : insensible à la casse et aux accents. */
const collator = new Intl.Collator("fr", { sensitivity: "base", numeric: true });
export function compareText(a: string, b: string): number {
  return collator.compare(a, b);
}

/** Recherche de texte insensible à la casse et aux accents. */
export function foldText(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase();
}

/** Les opérateurs de texte, communs à text, rich_text, url, email, phone. */
export function textOperators<T extends FieldType>(
  toText: (value: FieldValueMap[T]) => string,
): FilterOperator<T, string>[] {
  const fold = (value: FieldValueMap[T]) => foldText(toText(value));
  return [
    { id: "eq", operand: "text", test: (v, o) => fold(v) === foldText(o) },
    {
      id: "neq",
      operand: "text",
      matchesEmpty: true,
      test: (v, o) => fold(v) !== foldText(o),
    },
    {
      id: "contains",
      operand: "text",
      test: (v, o) => fold(v).includes(foldText(o)),
    },
    {
      id: "not_contains",
      operand: "text",
      matchesEmpty: true,
      test: (v, o) => !fold(v).includes(foldText(o)),
    },
    {
      id: "starts_with",
      operand: "text",
      test: (v, o) => fold(v).startsWith(foldText(o)),
    },
    {
      id: "ends_with",
      operand: "text",
      test: (v, o) => fold(v).endsWith(foldText(o)),
    },
  ];
}

export const TEXT_AGGREGATES: AggregateId[] = [
  "count",
  "count_empty",
  "count_not_empty",
  "count_unique",
  "percent_empty",
  "percent_not_empty",
];
